import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { getDealsQueryKey, type DealListItem, type DealStage, updateDealStage } from '../api/deals.client.js';
import { moveDealStage } from '../lib/kanban.js';

interface MutationInput {
  dealId: string;
  toStage: DealStage;
}

interface MutationContext {
  previousDeals: DealListItem[] | undefined;
  queryKey: readonly [string, string, string];
}

export function useDealStageMutation() {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryKey = getDealsQueryKey(workspaceId);

  return useMutation<DealListItem, Error, MutationInput, MutationContext>({
    mutationFn: async ({ dealId, toStage }) => updateDealStage(dealId, { toStage }),
    onMutate: async ({ dealId, toStage }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousDeals = queryClient.getQueryData<DealListItem[]>(queryKey);

      if (previousDeals !== undefined) {
        queryClient.setQueryData<DealListItem[]>(queryKey, moveDealStage(previousDeals, dealId, toStage));
      }

      return { previousDeals, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDeals !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousDeals);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}
